/**
 * Per-Flavor Content Switcher — combo PDPs
 * Reveals the content-block + nutrition group matching the selected Flavor option.
 *
 * Keys on FLAVOR, not variant id, so switching pack size (6/12/24) is a no-op here.
 * Detection matches nc-cost-per-can.js / nc-variant-description.js: observe
 * #callBackVariant class changes (the theme's swatch mechanism), with the
 * #product-select change event as fallback.
 */
(function () {
  function init() {
    const container = document.querySelector('[data-flavor-content-container]');
    if (!container || container.dataset.flavorContentInitialized === 'true') return;
    container.dataset.flavorContentInitialized = 'true';

  function playVideo(video) {
    const playAttempt = video.play();
    if (playAttempt && typeof playAttempt.catch === 'function') {
      playAttempt.catch(function () {
        // A later intersection retries if the browser temporarily blocks playback.
      });
    }
  }

  const videoObserver = 'IntersectionObserver' in window
    ? new IntersectionObserver(function (entries) {
        for (const entry of entries) {
          const video = entry.target;
          const group = video.closest('[data-flavor-content]');
          const groupIsVisible = !group || group.style.display !== 'none';

          if (entry.isIntersecting && groupIsVisible) {
            playVideo(video);
          } else if (!groupIsVisible) {
            video.pause();
          }
        }
      }, { threshold: 0.15 })
    : null;

  function prepareVideo(video) {
    video.controls = false;
    video.removeAttribute('controls');
    video.autoplay = true;
    video.loop = true;
    video.muted = true;
    video.defaultMuted = true;
    video.playsInline = true;
    video.preload = 'metadata';
    video.setAttribute('autoplay', '');
    video.setAttribute('loop', '');
    video.setAttribute('muted', '');
    video.setAttribute('playsinline', '');

    if (video.dataset.ncAutoplayReady === 'true') return;
    video.dataset.ncAutoplayReady = 'true';
    if (videoObserver) videoObserver.observe(video);
  }

  function syncVideos(target) {
    for (const video of container.querySelectorAll('video')) {
      prepareVideo(video);

      if (!target || !target.contains(video)) {
        video.pause();
        continue;
      }

      playVideo(video);
    }
  }

  const dataEl = document.querySelector('[data-flavor-content-data]');
  if (!dataEl) {
    syncVideos(container.querySelector('[data-flavor-content]'));
    return;
  }

  let variantFlavors;
  try {
    variantFlavors = JSON.parse(dataEl.textContent);
  } catch (e) {
    return;
  }

  const select = document.getElementById('product-select');
  let currentKey = null;

  /**
   * Only the server-rendered flavor is in the DOM; the rest sit in <template>.
   * Clone one in the first time its flavor is picked, in its original position so
   * DOM order still matches the flavor order. Subsequent switches are a plain toggle.
   */
  function ensureGroup(key) {
    const existing = container.querySelector('[data-flavor-content="' + key + '"]');
    if (existing) return existing;

    const tpl = container.querySelector('template[data-flavor-template="' + key + '"]');
    if (!tpl) return null;

    tpl.parentNode.insertBefore(tpl.content.cloneNode(true), tpl);
    tpl.remove(); // drop the source fragment so we are not holding two copies
    return container.querySelector('[data-flavor-content="' + key + '"]');
  }

  /**
   * Flavor groups stay in the DOM after their first selection. Keep the public
   * sourcing anchor on the visible group only, otherwise repeat visits create
   * duplicate IDs and #nc-sourcing-proof can resolve to a hidden, stale flavor.
   */
  function syncSourcingAnchor(target) {
    for (const proof of container.querySelectorAll('[data-sourcing-proof]')) {
      proof.removeAttribute('id');
    }

    const proof = target && target.querySelector('[data-sourcing-proof]');
    if (proof) proof.id = 'nc-sourcing-proof';
  }

  function update() {
    if (!select) return;
    const key = variantFlavors[select.value];
    if (!key || key === currentKey) return; // unknown variant, or size-only change

    const target = ensureGroup(key);
    if (!target) return; // no content for this flavor; leave what is showing
    currentKey = key;
    syncSourcingAnchor(target);

    for (const el of container.querySelectorAll('[data-flavor-content]')) {
      const isMatch = el === target;
      el.style.display = isMatch ? '' : 'none';
      el.classList.toggle('is-active', isMatch);
    }

    syncVideos(target);
  }

  // Seed currentKey from the server-rendered active group so the first real
  // flavor change is not swallowed by the no-op guard.
  const active = container.querySelector('[data-flavor-content][data-active="true"]');
  if (active) {
    currentKey = active.dataset.flavorContent;
    syncSourcingAnchor(active);
    syncVideos(active);
  }

  const cbv = document.getElementById('callBackVariant');
  if (cbv) {
    new MutationObserver(update).observe(cbv, { attributes: true, attributeFilter: ['class'] });
  }

  if (select) {
    select.addEventListener('change', update);
  }

  container.addEventListener('click', function (event) {
    const button = event.target.closest('[data-variety-health-prev], [data-variety-health-next]');
    if (!button || !container.contains(button)) return;

    const carousel = button.closest('[data-variety-health-carousel]');
    const track = carousel && carousel.querySelector('[data-variety-health-track]');
    if (!track) return;

    const direction = button.hasAttribute('data-variety-health-prev') ? -1 : 1;
    const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    track.scrollBy({
      left: direction * track.clientWidth,
      behavior: reducedMotion ? 'auto' : 'smooth'
    });
  });
  }

  init();
  // Combined Listings replaces #nathan-content, so the new flavor content
  // container needs the same observers and delegated handlers as first load.
  document.addEventListener('nc:combined-product:loaded', init);
})();
